import { useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { launchInternalAudit } from "@/lib/internal-audit.functions";
import { INVALIDATE } from "@/lib/query-registry";

const GOLD = "#C8A84B";
const labelCls = "text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const inputCls =
  "mt-1.5 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground focus:border-foreground focus:outline-none";

export const Route = createFileRoute("/admin/internal-audit")({
  head: () => ({
    meta: [
      { title: "Internal Audit · Shugert Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: InternalAuditPage,
});

function InternalAuditPage() {
  const launch = useServerFn(launchInternalAudit);
  const router = useRouter();
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const [state, setState] = useState({
    website_url: "",
    client_name: "",
    competitors: "",
    target_audience: "",
    notes: "",
    email_when_ready: false,
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="grid gap-6">
      <header>
        <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
          Internal · admin only
        </p>
        <h1 className="mt-2 font-display text-2xl font-semibold text-foreground">
          Run a free internal GEO audit
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Launches the full Pro pipeline against any URL. No Stripe, no credits, no customer email
          by default.
        </p>
      </header>

      <section className="rounded-2xl border border-border bg-card/30 p-5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between text-left"
        >
          <span className="font-display text-sm font-semibold text-foreground">How this works</span>
          <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
        </button>
        {open && (
          <ol className="mt-4 space-y-2 text-sm text-muted-foreground">
            <li>
              <strong className="text-foreground">1.</strong> Submit the form below, we create an
              order at <span className="font-mono">$0</span> with{" "}
              <span className="font-mono">plan_id=&quot;pro&quot;</span> and kick off the same audit
              pipeline a paid customer would get.
            </li>
            <li>
              <strong className="text-foreground">2.</strong> You're redirected to{" "}
              <span className="font-mono">/admin/orders/&#123;id&#125;</span> to watch progress
              (auto-refreshes every ~15s while processing).
            </li>
            <li>
              <strong className="text-foreground">3.</strong> When the audit lands, the order shows
              an <span className="font-mono">Internal</span> pill. Customer emails are suppressed
              unless you tick the box below.
            </li>
            <li>
              <strong className="text-foreground">4.</strong> To email the report out later (e.g.
              share with a prospect), use the existing <em>Deliver Report</em> button on the order
              page, it always works, even for internal audits.
            </li>
            <li>
              <strong className="text-foreground">5.</strong> Internal audits are excluded from{" "}
              <em>Revenue this month</em> and <em>Pending Pro review</em> counters on the dashboard,
              but still appear in the orders table.
            </li>
          </ol>
        )}
      </section>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setStatus("submitting");
          setError(null);
          try {
            const res = await launch({ data: state });
            if ("error" in res) throw new Error(res.error);
            toast.success(`Internal audit queued · ${res.order_number}`);
            await qc.invalidateQueries({ queryKey: INVALIDATE.adminOrders });
            router.navigate({ to: "/admin/orders/$id", params: { id: res.order_id } });
          } catch (err) {
            setStatus("error");
            setError(err instanceof Error ? err.message : "Could not launch audit");
          }
        }}
        className="grid gap-5 rounded-2xl border border-border bg-card/30 p-6"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="website_url" className={labelCls}>
              Website URL *
            </label>
            <input
              id="website_url"
              type="url"
              required
              placeholder="https://shugert.com.mx"
              value={state.website_url}
              onChange={(e) => setState({ ...state, website_url: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="client_name" className={labelCls}>
              Internal label *
            </label>
            <input
              id="client_name"
              required
              value={state.client_name}
              onChange={(e) => setState({ ...state, client_name: e.target.value })}
              className={inputCls}
              placeholder="Shugert internal, homepage Q4"
            />
          </div>
        </div>
        <div>
          <label htmlFor="competitors" className={labelCls}>
            Top 3 competitors
          </label>
          <input
            id="competitors"
            placeholder="competitor1.com, competitor2.com, competitor3.com"
            value={state.competitors}
            onChange={(e) => setState({ ...state, competitors: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="target_audience" className={labelCls}>
            Target audience
          </label>
          <input
            id="target_audience"
            value={state.target_audience}
            onChange={(e) => setState({ ...state, target_audience: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="notes" className={labelCls}>
            Internal notes
          </label>
          <textarea
            id="notes"
            rows={4}
            value={state.notes}
            onChange={(e) => setState({ ...state, notes: e.target.value })}
            className={inputCls}
          />
        </div>
        <label className="flex items-start gap-3 rounded-xl border border-border bg-background/40 p-4 text-sm">
          <input
            type="checkbox"
            checked={state.email_when_ready}
            onChange={(e) => setState({ ...state, email_when_ready: e.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="font-medium text-foreground">Email the report when ready</span>
            <span className="mt-1 block text-xs text-muted-foreground">
              Off by default. Leave unchecked for silent internal runs, you can still deliver
              manually later from the order page.
            </span>
          </span>
        </label>
        {error && (
          <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex items-center justify-between gap-3 pt-2">
          <Link
            to="/admin/dashboard"
            className="text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
          >
            ← Cancel
          </Link>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
          >
            {status === "submitting" ? "Launching…" : "Launch internal audit (free) →"}
          </button>
        </div>
      </form>
    </div>
  );
}
